/* Kap-Bak :: Simple Bundles BYOB enhancements.

   1. Pack image strip above the widget. The BYOB app collapses the theme's
      product gallery to zero height on bundle products, so the variant
      images never render. This rebuilds them from #kb-pack-data, which
      Liquid generates from the product itself.

      The strip is a SIBLING before <byob-bundle>, not a child. The app's
      stylesheet hides any element it does not own inside that tag. Padding
      is copied from .byob-bundle__root at runtime so it stays aligned.

   2. Instant pack switching, no page load.

      The app's own pack buttons are broken: they push the variant to the URL
      but never re-render. Setting the selectedVariantId PROPERTY is ignored
      too, and so is dispatching its packSelect event.

      What does work is setting the selected-variant-id ATTRIBUTE on
      <byob-bundle>. That fires the component's attributeChangedCallback and
      it re-renders properly: slot count, discount tier and totals all
      update. The active highlight on the pack buttons is the one thing it
      does not sync, so we set that ourselves on every <byob-pack>.

      switchPack() therefore does four things and never navigates:
        a) set the attribute, which does the real work
        b) mirror the active state onto both pack rows
        c) push the URL so the back button and sharing still behave
        d) repaint our own strip

      A navigation fallback stays in place: if the attribute approach ever
      stops working after an app update, the tile is still a real link, so
      the click falls through to a page load rather than doing nothing.

   3. Category tabs. The app stacks each category section vertically; this
      turns them into a tab bar under a heading.

   The widget is a Stencil app that re-renders on selection, so 1 and 3 are
   rebuilt by a MutationObserver. Selections live in the app's own state, not
   in the DOM, so hiding a section never loses a customer's picks. */
(function () {
  'use strict';

  var NS = 'kb-byob-tabs';
  var BAR = 'kb-byob-tabbar';
  var PACKS = 'kb-packs';
  var TABS_HEADING = 'Choose Your Category';
  var PACKS_HEADING = 'Choose Your Pack';
  var active = 0;

  /* ---------------------------------------------------------------
     PACK SWITCHING
     --------------------------------------------------------------- */

  function switchPack(variantId, url) {
    var host = document.querySelector('byob-bundle');
    if (!host || !variantId) return false;

    var before = host.getAttribute('selected-variant-id');

    // a) the attribute is what actually makes the component re-render
    host.setAttribute('selected-variant-id', String(variantId));

    if (host.getAttribute('selected-variant-id') !== String(variantId)) {
      return false; // refused, let the click navigate instead
    }

    // b) the component does not sync the active highlight itself
    Array.prototype.forEach.call(
      document.querySelectorAll('byob-pack'),
      function (p) {
        if (p.getAttribute('variant-id') === String(variantId)) {
          p.setAttribute('active', '');
        } else {
          p.removeAttribute('active');
        }
      }
    );

    // c) keep the URL honest so back, forward and sharing still work
    if (url && window.history && history.pushState) {
      try {
        history.pushState({ kbVariant: String(variantId) }, '', url);
      } catch (e) {}
    }

    // d) our own strip
    var strip = document.querySelector('.' + PACKS);
    if (strip) {
      markActive(strip, variantId);
      strip.dataset.active = String(variantId);
    }

    return before !== String(variantId) || true;
  }

  /* ---------------------------------------------------------------
     1. PACK IMAGE STRIP
     --------------------------------------------------------------- */

  function packData() {
    var el = document.getElementById('kb-pack-data');
    if (!el) return null;
    try {
      var rows = JSON.parse(el.textContent);
      return rows && rows.length > 1 ? rows : null;
    } catch (e) {
      return null;
    }
  }

  function activeVariantId() {
    var host = document.querySelector('byob-bundle');
    if (host && host.getAttribute('selected-variant-id')) {
      return host.getAttribute('selected-variant-id');
    }
    var flagged = document.querySelector('byob-pack[active]');
    if (flagged && flagged.getAttribute('variant-id')) {
      return flagged.getAttribute('variant-id');
    }
    return new URLSearchParams(window.location.search).get('variant');
  }

  function syncGutters() {
    var strip = document.querySelector('.' + PACKS);
    var root = document.querySelector('.byob-bundle__root');
    if (!strip || !root) return;
    var cs = getComputedStyle(root);
    strip.style.paddingLeft = cs.paddingLeft;
    strip.style.paddingRight = cs.paddingRight;
  }

  function markActive(wrap, current) {
    var items = wrap.querySelectorAll('.' + PACKS + '__item');
    Array.prototype.forEach.call(items, function (item) {
      var on = current && item.dataset.variantId === String(current);
      item.classList.toggle('is-active', !!on);
      if (on) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }
    });
  }

  function buildPacks() {
    var host = document.querySelector('byob-bundle');
    if (!host || !host.parentNode) return;

    var rows = packData();
    if (!rows) return;

    var current = activeVariantId();
    var existing = document.querySelector('.' + PACKS);

    if (existing) {
      if (existing.dataset.active !== String(current)) {
        markActive(existing, current);
        existing.dataset.active = String(current);
      }
      syncGutters();
      return;
    }

    var wrap = document.createElement('section');
    wrap.className = PACKS;
    wrap.dataset.active = String(current);
    wrap.setAttribute('aria-label', PACKS_HEADING);

    var heading = document.createElement('h2');
    heading.className = PACKS + '__heading';
    heading.textContent = PACKS_HEADING;
    wrap.appendChild(heading);

    var row = document.createElement('div');
    row.className = PACKS + '__row';

    rows.forEach(function (pack) {
      var item = document.createElement('a');
      item.className = PACKS + '__item';
      item.href = pack.url; // real link: fallback if switching ever fails
      item.dataset.variantId = pack.id;

      var media = document.createElement('span');
      media.className = PACKS + '__media';
      if (pack.image) {
        var img = document.createElement('img');
        img.src = pack.image;
        img.alt = pack.title;
        img.loading = 'lazy';
        media.appendChild(img);
      }
      item.appendChild(media);

      var label = document.createElement('span');
      label.className = PACKS + '__label';
      label.textContent = pack.title;
      item.appendChild(label);

      var price = document.createElement('span');
      price.className = PACKS + '__price';
      price.textContent = pack.price;
      item.appendChild(price);

      item.addEventListener('click', function (e) {
        // Leave modified clicks alone: new tab, new window, download.
        if (e.defaultPrevented || e.button !== 0) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

        if (item.classList.contains('is-active')) {
          e.preventDefault();
          return;
        }

        if (switchPack(pack.id, pack.url)) {
          e.preventDefault(); // handled in place, no page load
        }
        // otherwise fall through and let the browser navigate
      });

      row.appendChild(item);
    });

    wrap.appendChild(row);
    host.parentNode.insertBefore(wrap, host);

    markActive(wrap, current);
    syncGutters();
  }

  /* ---------------------------------------------------------------
     3. CATEGORY TABS
     --------------------------------------------------------------- */

  function sectionsIn(wrap) {
    return Array.prototype.slice.call(
      wrap.querySelectorAll(':scope > .byob-bundle__product-section')
    );
  }

  function showActive(sections) {
    sections.forEach(function (s, i) {
      if (i === active) {
        s.removeAttribute('hidden');
      } else {
        s.setAttribute('hidden', '');
      }
    });
  }

  function paint(nav) {
    Array.prototype.slice.call(nav.children).forEach(function (btn, i) {
      btn.setAttribute('aria-selected', i === active ? 'true' : 'false');
    });
  }

  function buildTabs() {
    var wrap = document.querySelector('.byob-bundle__products');
    if (!wrap) return;

    var sections = sectionsIn(wrap);
    if (sections.length < 2) return;

    var labels = sections.map(function (s) {
      var h = s.querySelector('.byob-bundle__product-section-title');
      return h ? h.textContent.trim() : 'Products';
    });

    var existing = wrap.parentNode.querySelector('.' + BAR);
    if (existing && existing.dataset.labels === labels.join('|')) {
      showActive(sections);
      return;
    }
    if (existing) existing.parentNode.removeChild(existing);

    if (active >= sections.length) active = 0;

    var bar = document.createElement('div');
    bar.className = BAR;
    bar.dataset.labels = labels.join('|');

    var heading = document.createElement('h2');
    heading.className = BAR + '__heading';
    heading.id = BAR + '-heading';
    heading.textContent = TABS_HEADING;
    bar.appendChild(heading);

    var nav = document.createElement('nav');
    nav.className = NS;
    nav.setAttribute('role', 'tablist');
    nav.setAttribute('aria-labelledby', heading.id);

    labels.forEach(function (label, i) {
      var count = sections[i].querySelectorAll('.byob-bundle__product-card').length;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = NS + '__btn';
      btn.setAttribute('role', 'tab');
      btn.textContent = label;

      var badge = document.createElement('span');
      badge.className = NS + '__count';
      badge.textContent = count;
      btn.appendChild(badge);

      btn.addEventListener('click', function () {
        active = i;
        showActive(sectionsIn(wrap));
        paint(nav);
      });

      nav.appendChild(btn);
    });

    bar.appendChild(nav);
    wrap.parentNode.insertBefore(bar, wrap);

    var root = document.querySelector('.byob-bundle__root');
    if (root) root.classList.add('kb-byob-tabbed');

    showActive(sections);
    paint(nav);
  }

  /* ---------------------------------------------------------------
     BOOT
     --------------------------------------------------------------- */

  function build() {
    buildPacks();
    buildTabs();
  }

  function boot() {
    build();

    var host = document.getElementById('byob-app-embed-wrapper');
    if (host && window.MutationObserver) {
      var timer;
      new MutationObserver(function () {
        clearTimeout(timer);
        timer = setTimeout(build, 120);
      }).observe(host, { childList: true, subtree: true });
    }

    var rt;
    window.addEventListener('resize', function () {
      clearTimeout(rt);
      rt = setTimeout(syncGutters, 150);
    });

    // Back and forward buttons now move between packs, so honour them.
    window.addEventListener('popstate', function () {
      var id = new URLSearchParams(window.location.search).get('variant');
      if (id) switchPack(id, null);
    });
  }

  function waitForWidget() {
    var tries = 0;
    var poll = setInterval(function () {
      tries += 1;
      if (document.querySelector('.byob-bundle__product-section') || tries > 80) {
        clearInterval(poll);
        boot();
      }
    }, 250);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForWidget);
  } else {
    waitForWidget();
  }
})();